import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TicketIcon, PlusCircleIcon, CheckCircle2Icon, CopyIcon, TrashIcon, PrinterIcon } from 'lucide-react';
interface Coupon {
  id: string;
  couponCode: string;
  vendor: string;
  amount: number;
  validTill: string;
  createdAt: string;
  status: 'Active' | 'Expired' | 'Used';
}
const vendors = ['Logistics Express Pvt Ltd', 'FastTrack Transport Co.', 'Metro Cargo Services', 'National Freight Lines', 'BlueDart Logistics', 'SafeMove Carriers'];
export function CouponGenerationPage() {
  const [vendor, setVendor] = useState('');
  const [amount, setAmount] = useState<number | ''>('');
  const [validTill, setValidTill] = useState('');
  const [coupons, setCoupons] = useState<Coupon[]>([{
    id: '1',
    couponCode: 'COUP-7823',
    vendor: 'Logistics Express Pvt Ltd',
    amount: 500,
    validTill: '2026-04-30',
    createdAt: '2026-04-10 10:30 AM',
    status: 'Active'
  }, {
    id: '2',
    couponCode: 'COUP-4519',
    vendor: 'FastTrack Transport Co.',
    amount: 1000,
    validTill: '2026-04-25',
    createdAt: '2026-04-08 02:15 PM',
    status: 'Active'
  }, {
    id: '3',
    couponCode: 'COUP-3102',
    vendor: 'Metro Cargo Services',
    amount: 250,
    validTill: '2026-04-12',
    createdAt: '2026-04-05 09:00 AM',
    status: 'Expired'
  }, {
    id: '4',
    couponCode: 'COUP-6745',
    vendor: 'National Freight Lines',
    amount: 750,
    validTill: '2026-04-20',
    createdAt: '2026-04-09 11:45 AM',
    status: 'Used'
  }]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastGeneratedCode, setLastGeneratedCode] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const handleGenerate = () => {
    if (!vendor || !amount || !validTill) return;
    const code = `COUP-${Math.floor(1000 + Math.random() * 9000)}`;
    const newCoupon: Coupon = {
      id: Date.now().toString(),
      couponCode: code,
      vendor,
      amount: Number(amount),
      validTill,
      createdAt: new Date().toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }),
      status: 'Active'
    };
    setCoupons([newCoupon, ...coupons]);
    setLastGeneratedCode(code);
    setShowSuccess(true);
    setVendor('');
    setAmount('');
    setValidTill('');
    setTimeout(() => setShowSuccess(false), 4000);
  };
  const handleCopy = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };
  const handleDelete = (id: string) => {
    setCoupons(coupons.filter(c => c.id !== id));
  };
  const handlePrint = (coupon: Coupon) => {
    const printContent = document.getElementById(`coupon-print-${coupon.id}`);
    if (!printContent) return;
    document.body.classList.add('print-thermal');
    window.print();
    document.body.classList.remove('print-thermal');
  };
  const statusBadge: Record<string, string> = {
    Active: 'bg-green-50 text-green-700 border-green-200',
    Expired: 'bg-red-50 text-red-600 border-red-200',
    Used: 'bg-slate-100 text-slate-500 border-slate-200'
  };
  return <div className="w-full max-w-5xl mx-auto space-y-6" data-id="element-367">
      {/* Generation Form */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-5 xs:p-6 sm:p-8" data-id="element-368">
        <div className="flex items-center gap-3 mb-6" data-id="element-369">
          <div className="bg-brand-teal/10 p-2 rounded-xl border border-brand-teal/20" data-id="element-370">
            <TicketIcon className="w-6 h-6 text-brand-teal" data-id="element-371" />
          </div>
          <div data-id="element-372">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight" data-id="element-373">
              Coupon Generation
            </h2>
            <p className="text-sm text-slate-500" data-id="element-374">
              Create payment coupons and assign them to vendors.
            </p>
          </div>
        </div>

        {/* Success Alert */}
        <AnimatePresence data-id="element-375">
          {showSuccess && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="mb-6 flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl" data-id="element-376">
              <CheckCircle2Icon className="w-5 h-5 text-green-600 shrink-0" data-id="element-377" />
              <div data-id="element-378">
                <p className="text-sm font-semibold text-green-800" data-id="element-379">
                  Coupon Generated Successfully
                </p>
                <p className="text-xs text-green-600" data-id="element-380">
                  Code:{' '}
                  <span className="font-mono font-bold" data-id="element-381">
                    {lastGeneratedCode}
                  </span>
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Form */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-id="element-382">
          <div data-id="element-383">
            <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-384">
              Assign to Vendor
            </label>
            <select value={vendor} onChange={e => setVendor(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all appearance-none" data-id="element-385">
              <option value="" data-id="element-386">-- Select Vendor --</option>
              {vendors.map(v => <option key={v} value={v} data-id="element-387">
                  {v}
                </option>)}
            </select>
          </div>
          <div data-id="element-388">
            <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-389">
              Amount (₹)
            </label>
            <input type="number" min="1" value={amount} onChange={e => setAmount(e.target.value ? Number(e.target.value) : '')} placeholder="e.g. 500" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all" data-id="element-390" />
          </div>
          <div data-id="element-391">
            <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-392">
              Valid Till
            </label>
            <input type="date" value={validTill} onChange={e => setValidTill(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all" data-id="element-393" />
          </div>
        </div>

        <button onClick={handleGenerate} disabled={!vendor || !amount || !validTill} className="inline-flex items-center justify-center gap-2 bg-brand-teal text-white px-6 py-3 rounded-xl font-medium text-sm hover:bg-brand-tealHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-394">
          <PlusCircleIcon className="w-4 h-4" data-id="element-395" />
          Generate Coupon
        </button>
      </motion.div>

      {/* Generated Coupons List */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.1
    }} className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-5 xs:p-6 sm:p-8" data-id="element-396">
        <div className="flex items-center justify-between mb-5" data-id="element-397">
          <h3 className="text-base font-bold text-slate-900" data-id="element-398">
            Generated Coupons
          </h3>
          <span className="text-xs font-medium text-slate-400" data-id="element-399">
            {coupons.length} coupons
          </span>
        </div>

        {coupons.length === 0 ? <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center bg-slate-50" data-id="element-400">
            <TicketIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" data-id="element-401" />
            <p className="text-slate-500 text-sm font-medium" data-id="element-402">
              No coupons generated yet
            </p>
          </div> : <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-403">
            <div className="overflow-x-auto" data-id="element-404">
              <table className="w-full text-left text-sm whitespace-nowrap" data-id="element-405">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500" data-id="element-406">
                  <tr data-id="element-407">
                    <th className="px-4 py-3 font-semibold" data-id="element-408">Coupon Code</th>
                    <th className="px-4 py-3 font-semibold" data-id="element-409">Vendor</th>
                    <th className="px-4 py-3 font-semibold text-right" data-id="element-410">
                      Amount (₹)
                    </th>
                    <th className="px-4 py-3 font-semibold" data-id="element-411">Valid Till</th>
                    <th className="px-4 py-3 font-semibold" data-id="element-412">Created</th>
                    <th className="px-4 py-3 font-semibold text-center" data-id="element-413">
                      Status
                    </th>
                    <th className="px-4 py-3 font-semibold text-center" data-id="element-414">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-415">
                  {coupons.map(coupon => <tr key={coupon.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-416">
                      <td className="px-4 py-3" data-id="element-417">
                        <span className="font-mono font-bold text-brand-teal bg-brand-teal/5 px-2 py-1 rounded-lg text-xs" data-id="element-418">
                          {coupon.couponCode}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700 font-medium text-xs max-w-[180px] truncate" data-id="element-419">
                        {coupon.vendor}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-slate-900" data-id="element-420">
                        ₹{coupon.amount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs" data-id="element-421">
                        {coupon.validTill}
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs" data-id="element-422">
                        {coupon.createdAt}
                      </td>
                      <td className="px-4 py-3 text-center" data-id="element-423">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium border ${statusBadge[coupon.status]}`} data-id="element-424">
                          {coupon.status}
                        </span>
                      </td>
                      <td className="px-4 py-3" data-id="element-425">
                        <div className="flex items-center justify-center gap-1" data-id="element-426">
                          <button onClick={() => handleCopy(coupon.couponCode, coupon.id)} className="p-1.5 text-slate-400 hover:text-brand-teal hover:bg-brand-teal/5 rounded-lg transition-colors" title="Copy code" data-id="element-427">
                            {copiedId === coupon.id ? <CheckCircle2Icon className="w-4 h-4 text-green-500" data-id="element-428" /> : <CopyIcon className="w-4 h-4" data-id="element-429" />}
                          </button>
                          <button onClick={() => handleDelete(coupon.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete" data-id="element-430">
                            <TrashIcon className="w-4 h-4" data-id="element-431" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </motion.div>
    </div>;
}